import React from "react";
import { View, Text, TouchableOpacity, StyleSheet } from "react-native";
import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { RainbowBorder } from "../../../common/native";
import { useOnboardingTips } from "../../../../contexts/OnboardingTipsContext";
import { TOOLTIP_STEPS } from "../../../../utils/constant";

type DateControlHeaderProps = {
  viewLabel: string;
  viewMode: "daily" | "weekly";
  toggleViewMode: () => void;
  onOpenRangePicker: () => void;
  onShiftRange: (delta: number) => void;
  onTodayPress: () => void;
  rangeOffset: number;
  tipsEnabled?: boolean;
};

const DateControlHeader: React.FC<DateControlHeaderProps> = ({
  viewLabel,
  viewMode,
  toggleViewMode,
  onOpenRangePicker,
  onShiftRange,
  onTodayPress,
  rangeOffset,
  tipsEnabled = false,
}) => {
  const getRangeLabelLines = (label: string) => {
    const commaYearMatch = label.match(/^(.*),\s*(\d{4})$/);
    if (commaYearMatch) {
      return { primary: commaYearMatch[1], secondary: commaYearMatch[2] };
    }

    const trailingYearMatch = label.match(/^(.*)\s(\d{4})$/);
    if (trailingYearMatch) {
      return { primary: trailingYearMatch[1], secondary: trailingYearMatch[2] };
    }

    return { primary: label, secondary: "" };
  };

  const { primary: rangeLabelPrimary, secondary: rangeLabelSecondary } =
    getRangeLabelLines(viewLabel);

  const { tipsState, markStepComplete } = useOnboardingTips();
  const canNavigateForward = rangeOffset < 0;

  return (
    <View style={styles.container}>
      <View style={styles.rangeControls}>
        <TouchableOpacity
          style={styles.rangeButton}
          onPress={onOpenRangePicker}
          accessibilityRole="button"
        >
          <Calendar size={14} color="#4B5563" />
          <View style={styles.rangeButtonLabelContainer}>
            <Text style={styles.rangeButtonText}>{rangeLabelPrimary}</Text>
            {!!rangeLabelSecondary && (
              <Text style={styles.rangeButtonSubText}>
                {rangeLabelSecondary}
              </Text>
            )}
          </View>
          <ChevronDown size={14} color="#4B5563" />
        </TouchableOpacity>

        <View style={styles.navGroup}>
          <TouchableOpacity
            style={styles.navButton}
            onPress={() => onShiftRange(-1)}
          >
            <ChevronLeft size={16} color="#111827" />
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.navButton,
              !canNavigateForward && styles.navButtonDisabled,
            ]}
            onPress={() => onShiftRange(1)}
            disabled={!canNavigateForward}
          >
            <ChevronRight
              size={16}
              color={canNavigateForward ? "#111827" : "#9CA3AF"}
            />
          </TouchableOpacity>
        </View>
      </View>

      <RainbowBorder
        isActive={
          tipsEnabled &&
          tipsState[TOOLTIP_STEPS.START_CONSULT] &&
          !tipsState[TOOLTIP_STEPS.SWITCH_VIEW]
        }
        borderRadius={8}
      >
        <TouchableOpacity
          style={styles.viewModeToggle}
          data-testid="view-mode-toggle"
          onPress={() => {
            toggleViewMode();
            markStepComplete(TOOLTIP_STEPS.SWITCH_VIEW);
          }}
        >
          <Text style={styles.viewModeToggleText}>Switch to</Text>
          <Text style={styles.viewModeToggleText}>
            {`${viewMode === "daily" ? "Weekly" : "Daily"} View`}
          </Text>
        </TouchableOpacity>
      </RainbowBorder>

      <TouchableOpacity
        style={styles.compactTodayButton}
        onPress={onTodayPress}
      >
        <Text style={styles.compactTodayButtonText}>Today</Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    columnGap: 12,
    rowGap: 8,
    flexWrap: "wrap",
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 6,
    width: "100%",
    minWidth: 0,
  },
  rangeControls: {
    flexDirection: "row",
    alignItems: "center",
    columnGap: 10,
    flexWrap: "wrap",
    minWidth: 0,
    flexShrink: 1,
    maxWidth: "100%",
  },
  rangeButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F5F5F5",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    alignSelf: "flex-start",
    minWidth: 0,
    maxWidth: "100%",
    flexShrink: 1,
  },
  rangeButtonText: {
    fontSize: 14,
    color: "#111827",
    fontWeight: "600",
    flexShrink: 1,
    textAlign: "left",
  },
  rangeButtonLabelContainer: {
    marginHorizontal: 6,
    minWidth: 0,
    flexShrink: 1,
  },
  rangeButtonSubText: {
    fontSize: 12,
    color: "#6B7280",
    fontWeight: "500",
    marginTop: 1,
  },
  navGroup: {
    flexDirection: "row",
    alignItems: "center",
    columnGap: 6,
    flexWrap: "wrap",
    minWidth: 0,
    maxWidth: "100%",
  },
  navButton: {
    width: 34,
    height: 32,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
  },
  navButtonDisabled: {
    opacity: 0.5,
  },
  viewModeToggle: {
    backgroundColor: "#F5F5F5",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    alignItems: "center",
    maxWidth: "100%",
    minWidth: 0,
  },
  viewModeToggleText: {
    fontSize: 14,
    color: "#555555",
    fontWeight: "500",
    textAlign: "center",
  },
  compactTodayButton: {
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
    alignSelf: "center",
  },
  compactTodayButtonText: {
    fontSize: 12,
    color: "#111827",
    fontWeight: "600",
  },
});

export default DateControlHeader;
